import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

import sessionReportApi from "../../hooks/GET/sessionReport";
import { formatLocalDateTime } from "../../utils/dateTime";

const rowsPerPageOptions = [10, 50, 100, 200];
const dateFilterOptions = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "custom", label: "Custom" },
];

const createDateFilterState = () => ({
  mode: "all",
  customStart: "",
  customEnd: "",
});

const getStartOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getEndOfDay = (value) => {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const getStartOfWeek = (value) => {
  const date = getStartOfDay(value);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
};

const getStartOfMonth = (value) => {
  const date = getStartOfDay(value);
  date.setDate(1);
  return date;
};

const getDateRangeByMode = (mode) => {
  const now = new Date();

  if (mode === "today") {
    return {
      start: getStartOfDay(now),
      end: getEndOfDay(now),
    };
  }

  if (mode === "week") {
    return {
      start: getStartOfWeek(now),
      end: getEndOfDay(now),
    };
  }

  if (mode === "month") {
    return {
      start: getStartOfMonth(now),
      end: getEndOfDay(now),
    };
  }

  return { start: null, end: null };
};

const matchesDateFilter = (value, filterState) => {
  if (filterState.mode === "all") {
    return true;
  }

  if (!value) {
    return false;
  }

  const dateValue = new Date(value);

  if (filterState.mode === "custom") {
    const start = filterState.customStart
      ? getStartOfDay(filterState.customStart)
      : null;
    const end = filterState.customEnd
      ? getEndOfDay(filterState.customEnd)
      : null;

    if (start && dateValue < start) {
      return false;
    }

    if (end && dateValue > end) {
      return false;
    }

    return true;
  }

  const { start, end } = getDateRangeByMode(filterState.mode);

  if (start && dateValue < start) {
    return false;
  }

  if (end && dateValue > end) {
    return false;
  }

  return true;
};

const formatDuration = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  const totalSeconds = Math.max(0, Math.floor(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(" ");
};

const formatDateFilterSummary = (filterState) => {
  if (filterState.mode === "all") {
    return "";
  }

  if (filterState.mode === "today") {
    return "Today";
  }

  if (filterState.mode === "week") {
    return "This week";
  }

  if (filterState.mode === "month") {
    return "This month";
  }

  if (filterState.mode === "custom") {
    if (filterState.customStart && filterState.customEnd) {
      return `Custom: ${filterState.customStart} to ${filterState.customEnd}`;
    }

    if (filterState.customStart) {
      return `Custom: from ${filterState.customStart}`;
    }

    if (filterState.customEnd) {
      return `Custom: until ${filterState.customEnd}`;
    }

    return "Custom";
  }

  return "";
};

const sortActivities = (activities) =>
  [...activities].sort((left, right) => {
    const leftTime = left.reportingStartAtUtc
      ? new Date(left.reportingStartAtUtc).getTime()
      : 0;
    const rightTime = right.reportingStartAtUtc
      ? new Date(right.reportingStartAtUtc).getTime()
      : 0;

    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }

    return (right.activityId || 0) - (left.activityId || 0);
  });

const formatPeriodLabel = (period) => {
  if (!period.start || !period.end) {
    return "Showing current result set";
  }

  if (period.isDefaultLast7Days) {
    return "Showing last 7 days";
  }

  return `Showing ${period.start.slice(0, 10)} to ${period.end.slice(0, 10)}`;
};

const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

function SessionTracking() {
  const [sessions, setSessions] = useState([]);
  const [reportPeriod, setReportPeriod] = useState({
    start: null,
    end: null,
    isDefaultLast7Days: true,
  });
  const [loading, setLoading] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [selectedReportName, setSelectedReportName] = useState("all");
  const [selectedSessionStatus, setSelectedSessionStatus] = useState("all");
  const [dateFilters, setDateFilters] = useState({
    loginAtUtc: createDateFilterState(),
    logoutAtUtc: createDateFilterState(),
    reportingStartAtUtc: createDateFilterState(),
    reportingEndAtUtc: createDateFilterState(),
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [openFilterKey, setOpenFilterKey] = useState("");
  const [periodInputs, setPeriodInputs] = useState({
    startDate: "",
    endDate: "",
  });
  const [appliedPeriod, setAppliedPeriod] = useState({
    startDate: "",
    endDate: "",
  });
  const [expandedSessions, setExpandedSessions] = useState({});
  const filterControlRefs = useRef({});

  useEffect(() => {
    setLoading(true);

    sessionReportApi({
      page,
      pageSize: rowsPerPage,
      startDate: appliedPeriod.startDate,
      endDate: appliedPeriod.endDate,
    })
      .then((data) => {
        setSessions(data.items);
        setTotalSessions(data.pagination.totalSessions);
        setTotalPages(data.pagination.totalPages);
        setReportPeriod(data.period);
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [appliedPeriod.endDate, appliedPeriod.startDate, page, refetchKey, rowsPerPage]);

  useEffect(() => {
    setExpandedSessions((currentValue) => {
      const nextValue = {};

      sessions.forEach((session) => {
        if (currentValue[session.sessionId]) {
          nextValue[session.sessionId] = true;
        }
      });

      return nextValue;
    });
  }, [sessions]);

  useEffect(() => {
    setPeriodInputs({
      startDate: toDateInputValue(reportPeriod.start),
      endDate: toDateInputValue(reportPeriod.end),
    });
  }, [reportPeriod.end, reportPeriod.start]);

  const userOptions = useMemo(
    () =>
      Array.from(
        new Map(
          sessions.map((session) => [
            session.userId,
            {
              id: session.userId,
              label: `${session.userFullName} (${session.userEmail})`,
            },
          ])
        ).values()
      ),
    [sessions]
  );

  const reportOptions = useMemo(
    () =>
      Array.from(
        new Set(
          sessions
            .flatMap((session) => session.activities || [])
            .map((activity) => activity.reportDisplayName)
            .filter((reportDisplayName) => reportDisplayName)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [sessions]
  );

  const sessionStatusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          sessions
            .map((session) => session.sessionStatus)
            .filter((sessionStatus) => sessionStatus)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [sessions]
  );

  const selectedUserLabel = useMemo(() => {
    if (selectedUserId === "all") {
      return "";
    }

    const selectedUser = userOptions.find(
      (option) => String(option.id) === selectedUserId
    );

    if (!selectedUser) {
      return "";
    }

    const [userName] = selectedUser.label.split(" (");
    return userName;
  }, [selectedUserId, userOptions]);
  const hasDateFilterApplied = (fieldName) => {
    const filterState = dateFilters[fieldName];
    return (
      filterState.mode !== "all" ||
      Boolean(filterState.customStart) ||
      Boolean(filterState.customEnd)
    );
  };

  const isFilterActive = (filterKey) => {
    if (filterKey === "user") {
      return selectedUserId !== "all";
    }

    if (filterKey === "report") {
      return selectedReportName !== "all";
    }

    if (filterKey === "sessionStatus") {
      return selectedSessionStatus !== "all";
    }

    return hasDateFilterApplied(filterKey);
  };

  const hasAnyFilterApplied =
    selectedUserId !== "all" ||
    selectedReportName !== "all" ||
    selectedSessionStatus !== "all" ||
    hasDateFilterApplied("loginAtUtc") ||
    hasDateFilterApplied("logoutAtUtc") ||
    hasDateFilterApplied("reportingStartAtUtc") ||
    hasDateFilterApplied("reportingEndAtUtc");

  const getFilterRowDisplayValue = (filterKey) => {
    if (filterKey === "user") {
      return selectedUserLabel;
    }

    if (filterKey === "report") {
      return selectedReportName !== "all" ? selectedReportName : "";
    }

    if (filterKey === "sessionStatus") {
      return selectedSessionStatus !== "all" ? selectedSessionStatus : "";
    }

    return formatDateFilterSummary(dateFilters[filterKey]);
  };

  const getFilterTooltip = (filterKey, label) => {
    if (!isFilterActive(filterKey)) {
      return "";
    }

    const value = getFilterRowDisplayValue(filterKey);
    return value ? `Filtering by ${label} - ${value}` : "";
  };

  const filteredSessions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const hasActivityFilters =
      selectedReportName !== "all" ||
      hasDateFilterApplied("reportingStartAtUtc") ||
      hasDateFilterApplied("reportingEndAtUtc");

    return sessions
      .map((session) => {
        const baseActivities = sortActivities(session.activities || []);
        const activityFiltered = baseActivities.filter((activity) => {
          const matchesReport =
            selectedReportName === "all" ||
            activity.reportDisplayName === selectedReportName;

          return (
            matchesReport &&
            matchesDateFilter(
              activity.reportingStartAtUtc,
              dateFilters.reportingStartAtUtc
            ) &&
            matchesDateFilter(
              activity.reportingEndAtUtc,
              dateFilters.reportingEndAtUtc
            )
          );
        });

        const matchesUser =
          selectedUserId === "all" || String(session.userId) === selectedUserId;

        const matchesSessionStatus =
          selectedSessionStatus === "all" ||
          session.sessionStatus === selectedSessionStatus;

        const matchesLoginAt = matchesDateFilter(
          session.loginAtUtc,
          dateFilters.loginAtUtc
        );
        const matchesLogoutAt = matchesDateFilter(
          session.logoutAtUtc,
          dateFilters.logoutAtUtc
        );

        if (
          !matchesUser ||
          !matchesSessionStatus ||
          !matchesLoginAt ||
          !matchesLogoutAt
        ) {
          return null;
        }

        const sessionSearchText = [
          session.sessionId,
          session.userFullName,
          session.userEmail,
          session.sessionStatus,
          formatLocalDateTime(session.loginAtUtc),
          formatLocalDateTime(session.logoutAtUtc),
        ]
          .join(" ")
          .toLowerCase();

        const searchMatchesSession =
          !normalizedSearch || sessionSearchText.includes(normalizedSearch);

        let visibleActivities = activityFiltered;

        if (normalizedSearch) {
          const activitySearchMatches = activityFiltered.filter((activity) =>
            [
              activity.reportDisplayName,
              formatLocalDateTime(activity.reportingStartAtUtc),
              formatLocalDateTime(activity.reportingEndAtUtc),
              formatDuration(activity.durationSeconds),
            ]
              .join(" ")
              .toLowerCase()
              .includes(normalizedSearch)
          );

          visibleActivities = searchMatchesSession
            ? activityFiltered
            : activitySearchMatches;
        }

        if (!searchMatchesSession && normalizedSearch && !visibleActivities.length) {
          return null;
        }

        if (hasActivityFilters && !visibleActivities.length) {
          return null;
        }

        const latestActivity = visibleActivities[0] || null;

        return {
          ...session,
          visibleActivities,
          latestActivity,
        };
      })
      .filter(Boolean);
  }, [
    dateFilters,
    search,
    selectedReportName,
    selectedSessionStatus,
    selectedUserId,
    sessions,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleDateFilterModeChange = (fieldName, value) => {
    setDateFilters((currentValue) => ({
      ...currentValue,
      [fieldName]: {
        mode: value,
        customStart:
          value === "custom" ? currentValue[fieldName].customStart : "",
        customEnd: value === "custom" ? currentValue[fieldName].customEnd : "",
      },
    }));
  };

  const handleDateFilterValueChange = (fieldName, valueName, value) => {
    setDateFilters((currentValue) => {
      const nextFilter = {
        ...currentValue[fieldName],
        [valueName]: value,
      };

      if (
        valueName === "customStart" &&
        nextFilter.customEnd &&
        value &&
        value > nextFilter.customEnd
      ) {
        nextFilter.customEnd = value;
      }

      if (
        valueName === "customEnd" &&
        nextFilter.customStart &&
        value &&
        value < nextFilter.customStart
      ) {
        nextFilter.customStart = value;
      }

      return {
        ...currentValue,
        [fieldName]: nextFilter,
      };
    });
  };

  const handleOpenFilter = (event, filterKey) => {
    const anchorElement =
      (hasAnyFilterApplied && filterControlRefs.current[filterKey]) ||
      event.currentTarget;
    setFilterAnchorEl(anchorElement);
    setOpenFilterKey(filterKey);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setOpenFilterKey("");
  };

  const resetDateFilter = (fieldName) => {
    setDateFilters((currentValue) => ({
      ...currentValue,
      [fieldName]: createDateFilterState(),
    }));
  };

  const clearColumnFilter = (filterKey) => {
    if (filterKey === "user") {
      setSelectedUserId("all");
    } else if (filterKey === "report") {
      setSelectedReportName("all");
    } else if (filterKey === "sessionStatus") {
      setSelectedSessionStatus("all");
    } else {
      setDateFilters((currentValue) => ({
        ...currentValue,
        [filterKey]: createDateFilterState(),
      }));
    }

    if (openFilterKey === filterKey) {
      handleCloseFilter();
    }
  };

  const toggleSessionExpansion = (sessionId) => {
    setExpandedSessions((currentValue) => ({
      ...currentValue,
      [sessionId]: !currentValue[sessionId],
    }));
  };

  const handleApplyPeriod = () => {
    setAppliedPeriod({
      startDate: periodInputs.startDate,
      endDate: periodInputs.endDate,
    });
    setPage(1);
  };

  const handleResetPeriod = () => {
    setAppliedPeriod({
      startDate: "",
      endDate: "",
    });
    setPage(1);
  };
  const renderHeaderLabel = (label, filterKey) => (
    <Tooltip
      title={getFilterTooltip(filterKey, label)}
      disableHoverListener={!isFilterActive(filterKey)}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        sx={{ whiteSpace: "nowrap" }}
      >
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <IconButton
          size="small"
          disableRipple
          onClick={(event) => handleOpenFilter(event, filterKey)}
          color={isFilterActive(filterKey) ? "primary" : "default"}
          sx={{
            p: 0,
            minWidth: "auto",
            borderRadius: 0,
            color: isFilterActive(filterKey) ? "primary.main" : "text.secondary",
            bgcolor: "transparent",
            "&:hover": {
              bgcolor: "transparent",
              color: isFilterActive(filterKey)
                ? "primary.dark"
                : "text.primary",
            },
          }}
        >
          {isFilterActive(filterKey) ? (
            <FilterAltIcon fontSize="small" />
          ) : (
            <FilterListIcon fontSize="small" />
          )}
        </IconButton>
      </Stack>
    </Tooltip>
  );

  const renderFilterRowControl = (filterKey, placeholder) => (
    <TextField
      fullWidth
      size="small"
      variant="standard"
      value={getFilterRowDisplayValue(filterKey)}
      placeholder={placeholder}
      inputRef={(element) => {
        filterControlRefs.current[filterKey] = element;
      }}
      onClick={(event) => handleOpenFilter(event, filterKey)}
      InputProps={{
        readOnly: true,
        disableUnderline: false,
        endAdornment: (
          <InputAdornment position="end">
            {isFilterActive(filterKey) && (
              <IconButton
                size="small"
                sx={{ p: 0, mr: 0.25 }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  clearColumnFilter(filterKey);
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <KeyboardArrowDownIcon
              fontSize="small"
              color={isFilterActive(filterKey) ? "primary" : "action"}
            />
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiInputBase-root": {
          px: 0.5,
          py: 0.5,
          cursor: "pointer",
        },
        "& .MuiInputBase-input": {
          cursor: "pointer",
          fontSize: 13,
          lineHeight: 1.4,
          textAlign: "left",
          color: isFilterActive(filterKey) ? "text.primary" : "text.secondary",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "text.secondary",
          opacity: 1,
        },
        "& .MuiInput-underline:before": {
          borderBottomColor: "divider",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottomColor: isFilterActive(filterKey)
            ? "primary.main"
            : "text.primary",
        },
        "& .MuiInput-underline:after": {
          borderBottomColor: isFilterActive(filterKey)
            ? "primary.main"
            : "text.primary",
        },
      }}
    />
  );

  const renderPopoverContent = () => {
    if (openFilterKey === "user") {
      return (
        <Stack spacing={1.5} sx={{ width: 260, p: 2 }}>
          <Typography variant="subtitle2">User</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedUserId}
            onChange={(event) => {
              setSelectedUserId(event.target.value);
            }}
          >
            <MenuItem value="all">All users</MenuItem>
            {userOptions.map((option) => (
              <MenuItem key={option.id} value={String(option.id)}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      );
    }

    if (openFilterKey === "report") {
      return (
        <Stack spacing={1.5} sx={{ width: 240, p: 2 }}>
          <Typography variant="subtitle2">Report</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedReportName}
            onChange={(event) => {
              setSelectedReportName(event.target.value);
            }}
          >
            <MenuItem value="all">All reports</MenuItem>
            {reportOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      );
    }

    if (openFilterKey === "sessionStatus") {
      return (
        <Stack spacing={1.5} sx={{ width: 220, p: 2 }}>
          <Typography variant="subtitle2">Session Status</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedSessionStatus}
            onChange={(event) => {
              setSelectedSessionStatus(event.target.value);
            }}
          >
            <MenuItem value="all">All status</MenuItem>
            {sessionStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      );
    }

    if (
      openFilterKey === "loginAtUtc" ||
      openFilterKey === "logoutAtUtc" ||
      openFilterKey === "reportingStartAtUtc" ||
      openFilterKey === "reportingEndAtUtc"
    ) {
      const labelMap = {
        loginAtUtc: "Login Time",
        logoutAtUtc: "Logout Time",
        reportingStartAtUtc: "Start Time",
        reportingEndAtUtc: "End Time",
      };

      return (
        <Stack spacing={1.5} sx={{ width: 260, p: 2 }}>
          <Typography variant="subtitle2">{labelMap[openFilterKey]}</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={dateFilters[openFilterKey].mode}
            onChange={(event) =>
              handleDateFilterModeChange(openFilterKey, event.target.value)
            }
          >
            {dateFilterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {dateFilters[openFilterKey].mode === "custom" && (
            <Stack spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Start date"
                type="date"
                value={dateFilters[openFilterKey].customStart}
                onChange={(event) =>
                  handleDateFilterValueChange(
                    openFilterKey,
                    "customStart",
                    event.target.value
                  )
                }
                inputProps={{
                  max: dateFilters[openFilterKey].customEnd || undefined,
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                label="End date"
                type="date"
                value={dateFilters[openFilterKey].customEnd}
                onChange={(event) =>
                  handleDateFilterValueChange(
                    openFilterKey,
                    "customEnd",
                    event.target.value
                  )
                }
                inputProps={{
                  min: dateFilters[openFilterKey].customStart || undefined,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button size="small" onClick={() => resetDateFilter(openFilterKey)}>
              Clear
            </Button>
          </Box>
        </Stack>
      );
    }

    return null;
  };
  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              gap: 1,
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            <Typography sx={{ display: "contents" }}>Show</Typography>
            <TextField
              select
              name="rowsPerPage"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ minWidth: 75 }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ display: "contents" }}>sessions</Typography>
            <TextField
              name="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
            <TextField
              size="small"
              label="Start date"
              type="date"
              value={periodInputs.startDate}
              onChange={(event) =>
                setPeriodInputs((currentValue) => {
                  const nextStartDate = event.target.value;
                  const nextEndDate =
                    currentValue.endDate &&
                    nextStartDate &&
                    nextStartDate > currentValue.endDate
                      ? nextStartDate
                      : currentValue.endDate;

                  return {
                    startDate: nextStartDate,
                    endDate: nextEndDate,
                  };
                })
              }
              inputProps={{
                max: periodInputs.endDate || undefined,
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="End date"
              type="date"
              value={periodInputs.endDate}
              onChange={(event) =>
                setPeriodInputs((currentValue) => {
                  const nextEndDate = event.target.value;
                  const nextStartDate =
                    currentValue.startDate &&
                    nextEndDate &&
                    nextEndDate < currentValue.startDate
                      ? nextEndDate
                      : currentValue.startDate;

                  return {
                    startDate: nextStartDate,
                    endDate: nextEndDate,
                  };
                })
              }
              inputProps={{
                min: periodInputs.startDate || undefined,
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <Tooltip title="Apply period">
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyPeriod}
                sx={{ height: 40 }}
              >
                Apply
              </Button>
            </Tooltip>
            <Tooltip title="Reset period">
              <Button
                size="small"
                variant="outlined"
                onClick={handleResetPeriod}
                sx={{ height: 40 }}
              >
                Reset
              </Button>
            </Tooltip>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                alignSelf: "center",
              }}
            >
              {formatPeriodLabel(reportPeriod)}
            </Typography>
          </Box>
          <Box sx={{ ml: "auto" }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => setRefetchKey((value) => value + 1)}
              sx={{ height: 40 }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: 64 }} />
              <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" fontWeight={600}>
                  Session ID
                </Typography>
              </TableCell>
              <TableCell align="center">{renderHeaderLabel("User", "user")}</TableCell>
              <TableCell align="center">
                {renderHeaderLabel("Login Time", "loginAtUtc")}
              </TableCell>
              <TableCell align="center">
                {renderHeaderLabel("Logout Time", "logoutAtUtc")}
              </TableCell>
              <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                {renderHeaderLabel("Session Status", "sessionStatus")}
              </TableCell>
              <TableCell align="center">{renderHeaderLabel("Report", "report")}</TableCell>
              {/* <TableCell align="center">
                {renderHeaderLabel("Start Time", "reportingStartAtUtc")}
              </TableCell>
              <TableCell align="center">
                {renderHeaderLabel("End Time", "reportingEndAtUtc")}
              </TableCell>
              <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" fontWeight={600}>
                  Duration
                </Typography>
              </TableCell> */}
            </TableRow>
            {hasAnyFilterApplied && (
              <TableRow>
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }} />
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }} />
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl("user", "Filter by User")}
                </TableCell>
              <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl("loginAtUtc", "Filter by Login Time")}
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl("logoutAtUtc", "Filter by Logout Time")}
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl(
                    "sessionStatus",
                    "Filter by Session Status"
                  )}
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl("report", "Filter by Report")}
                </TableCell>
                {/* <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl(
                    "reportingStartAtUtc",
                    "Filter by Start Time"
                  )}
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }}>
                  {renderFilterRowControl("reportingEndAtUtc", "Filter by End Time")}
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: "action.hover", py: 1 }} /> */}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  Loading session tracking data...
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filteredSessions.map((session) => {
                const isExpanded = Boolean(expandedSessions[session.sessionId]);
                const activityCount = Array.isArray(session.activities)
                  ? session.activities.length
                  : 0;
                const reportSummary =
                  activityCount === 1 ? "1 report" : `${activityCount} reports`;

                return (
                  <React.Fragment key={session.sessionId}>
                    <TableRow hover>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => toggleSessionExpansion(session.sessionId)}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">{session.sessionId}</TableCell>
                      <TableCell align="center">
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{session.userFullName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {session.userEmail}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {formatLocalDateTime(session.loginAtUtc)}
                      </TableCell>
                      <TableCell align="center">
                        {formatLocalDateTime(session.logoutAtUtc)}
                      </TableCell>
                      <TableCell align="center">{session.sessionStatus || "-"}</TableCell>
                      <TableCell align="center">{reportSummary}</TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ py: 0, px: 2, bgcolor: "action.hover" }}
                        >
                          <Box sx={{ py: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                              Report Activities
                            </Typography>
                            {!session.visibleActivities.length ? (
                              <Typography variant="body2" color="text.secondary">
                                No report activity available for this session.
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell align="center">Report</TableCell>
                                    <TableCell align="center">Start Time</TableCell>
                                    <TableCell align="center">End Time</TableCell>
                                    <TableCell align="center">Duration</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {session.visibleActivities.map((activity) => (
                                    <TableRow key={activity.activityId}>
                                      <TableCell align="center">
                                        {activity.reportDisplayName || "-"}
                                      </TableCell>
                                      <TableCell align="center">
                                        {formatLocalDateTime(activity.reportingStartAtUtc)}
                                      </TableCell>
                                      <TableCell align="center">
                                        {formatLocalDateTime(activity.reportingEndAtUtc)}
                                      </TableCell>
                                      <TableCell align="center">
                                        {formatDuration(activity.durationSeconds)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            {!loading && !filteredSessions.length && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  No session activity available to display
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {renderPopoverContent()}
      </Popover>

      {totalSessions > 0 && (
        <Grid container justifyContent="center" sx={{ mt: 2 }} spacing={1}>
          <Grid item>
            <Button
              size="small"
              onClick={(event) => handleChangePage(event, page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
          </Grid>
          <Grid item>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              variant="outlined"
              shape="rounded"
              color="secondary"
              hidePrevButton
              hideNextButton
            />
          </Grid>
          <Grid item>
            <Button
              size="small"
              onClick={(event) => handleChangePage(event, page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default SessionTracking;
